
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CancelledError, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  RefreshCw,
  Bell,
  Calendar,
  Zap,
  Plus,
  FileText,
  Users,
  Package,
  Clock as ClockIcon,
  QrCode,
  TrendingUp,
  ChefHat,
  ShoppingBag,
  DollarSign,
  BarChart3,
  Loader2,
  Smartphone,
  X,
  SlidersHorizontal,
} from "lucide-react";

import { useTranslation } from "react-i18next";
import { useLocaleFormatter } from "@/lib/i18n/useLocaleFormatter";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import { useTenant } from "@/hooks/useTenant";
import { useTenantPath } from "@/hooks/useTenantPath";
import { useOrderEvents } from "@/hooks/useOrderEvents";
import { useToast } from "@/hooks/use-toast";
import { isAbortError } from "@/lib/api/errors";
import { isUnauthenticatedError } from "@/lib/utils/errors";
import {
  listTables,
  fetchTableActivitySummary,
  type RestaurantTable,
  type TableActivitySummaryResponse,
} from "@/lib/api/tables";
import { fetchPayments, type PaymentRecord as ApiPaymentRecord } from "@/lib/api/payments";
import { fetchSalesReport, fetchItemsReport, type SalesReportResponse, type ItemsReportResponse } from "@/lib/api/reports";
import { fetchQrMetrics, type QrMetricsResponse } from "@/lib/api/qr";
import { fetchStaff, type StaffMember } from "@/lib/api/staff";
import { fetchOrders, payOrderCash, updateOrderStatus, type OrderStatus, type OrderSummary, type StationSnapshot } from "@/lib/api/orders";
import { fetchTenantLocations, type TenantLocationRecord } from "@/lib/api/settings";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { MetricsCards } from "@/features/restaurant/components/dashboard-v3/MetricsCards";
import { QuickActions as DashboardQuickActions } from "@/features/restaurant/components/dashboard-v3/QuickActions";
import { QRRestaurantManager } from "@/features/restaurant/components/dashboard-v3/QRRestaurantManager";
import { RecentOrders } from "@/features/restaurant/components/dashboard-v3/RecentOrders";
import { SalesChart } from "@/features/restaurant/components/dashboard-v3/SalesChart";
import { TopPerformers } from "@/features/restaurant/components/dashboard-v3/TopPerformers";
import { QrOrderingStatusCard } from "@/features/restaurant/components/qr/QrOrderingStatusCard";
import { buildTableQueueOrdering } from "@/features/restaurant/utils/tableQueueOrdering";
import type {
  DashboardMetric,
  QuickAction,
  RecentOrderRow,
  TableSummary,
  TablePaymentStatus,
  LiveStatusData,
  PerformanceMetrics,
  SalesPoint,
  HourlyPoint,
  TopItemRow,
} from "@/features/restaurant/components/dashboard-v3/types";
import { useRestaurantSettings } from "@/features/restaurant/hooks/useRestaurantSettings";
import { useTenantSlug } from "@/stores/tenantStore";

const DASHBOARD_QUERY_ROOT = ["dashboard-v3"] as const;

const dayFormatter = new Intl.DateTimeFormat(undefined, { weekday: "short" });
const hourFormatter = new Intl.DateTimeFormat(undefined, {
  hour: "numeric",
  hour12: true,
});

const WAITING_STATUSES = new Set(['PENDING_PAYMENT', 'PAID', 'ACCEPTED', 'IN_PROGRESS']);
const COMPLETED_STATUSES = new Set(['SERVED', 'PICKED_UP', 'COMPLETED']);
const ACCEPTABLE_ALERT_STATUSES = new Set<OrderStatus>(['PENDING_PAYMENT', 'PAID']);
const DASHBOARD_ORDERS_LIMIT = 200;

const isIgnorableQueryError = (error: unknown): boolean =>
  isAbortError(error) || error instanceof CancelledError;

const startOfDay = (date: Date) => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const endOfDay = (date: Date) => {
  const copy = new Date(date);
  copy.setHours(23, 59, 59, 999);
  return copy;
};

const subtractDays = (date: Date, amount: number) => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() - amount);
  return copy;
};

const isoDate = (date: Date) => date.toISOString().slice(0, 10);

const calculatePercentChange = (current: number, previous: number) => {
  if (previous === 0) {
    return current === 0 ? 0 : 100;
  }
  return ((current - previous) / Math.abs(previous)) * 100;
};

const clampPercent = (value: number) => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.min(100, value));
};

const formatDelta = (value: number, suffix = "%") => {
  if (!Number.isFinite(value) || Math.abs(value) < 0.1) {
    return `0${suffix}`;
  }
  const prefix = value > 0 ? "+" : "";
  return prefix + value.toFixed(1) + suffix;
};

const determineTrend = (value: number): "up" | "down" | "neutral" => {
  if (value > 1) return "up";
  if (value < -1) return "down";
  return "neutral";
};

const formatStatusLabel = (status: string) =>
  status
    .toLowerCase()
    .split("_")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");

type NormalizedOrderPaymentStatus = Exclude<TablePaymentStatus, 'cash'>;
type NormalizedPaymentRecordStatus = Exclude<TablePaymentStatus, 'cash' | 'none'> | null;

const PAYMENT_SUCCESS_STATUSES = new Set(['PAID', 'SETTLED', 'REFUNDED', 'VOIDED']);
const PAYMENT_FAILURE_STATUSES = new Set(['FAILED', 'DECLINED', 'CANCELLED', 'CANCELED', 'ERROR']);
const PAYMENT_PENDING_STATUSES = new Set(['PENDING', 'UNPAID', 'AWAITING', 'REQUIRES_ACTION', 'REQUIRES_PAYMENT', 'PROCESSING']);

const PAYMENT_RECORD_SUCCESS_STATUSES = new Set(['completed', 'succeeded', 'captured', 'refunded', 'voided']);
const PAYMENT_RECORD_FAILURE_STATUSES = new Set(['failed', 'declined', 'canceled', 'cancelled', 'error']);
const PAYMENT_RECORD_PENDING_STATUSES = new Set(['pending', 'authorized', 'requires_action', 'processing', 'held']);

const normalizePaymentMethod = (method?: string | null): 'cash' | 'card' | 'unknown' => {
  if (!method) {
    return 'unknown';
  }
  const value = method.toLowerCase();
  if (value.includes('cash')) {
    return 'cash';
  }
  if (
    value.includes('card') ||
    value.includes('visa') ||
    value.includes('mastercard') ||
    value.includes('amex') ||
    value.includes('wallet') ||
    value.includes('pay') ||
    value.includes('upi')
  ) {
    return 'card';
  }
  return 'unknown';
};

const normalizeOrderPaymentStatus = (status?: string | null): NormalizedOrderPaymentStatus => {
  if (!status) {
    return 'none';
  }
  const value = status.trim().toUpperCase();
  if (PAYMENT_SUCCESS_STATUSES.has(value)) {
    return 'paid';
  }
  if (PAYMENT_FAILURE_STATUSES.has(value)) {
    return 'failed';
  }
  if (PAYMENT_PENDING_STATUSES.has(value)) {
    return 'pending';
  }
  return 'none';
};

const normalizePaymentRecordStatus = (payment?: ApiPaymentRecord | null): NormalizedPaymentRecordStatus => {
  if (!payment || !payment.status) {
    return null;
  }
  const value = payment.status.toLowerCase();
  if (PAYMENT_RECORD_SUCCESS_STATUSES.has(value)) {
    return 'paid';
  }
  if (PAYMENT_RECORD_FAILURE_STATUSES.has(value)) {
    return 'failed';
  }
  if (PAYMENT_RECORD_PENDING_STATUSES.has(value)) {
    return 'pending';
  }
  return null;
};

const resolveTablePaymentStatus = ({
  paymentRecordStatus,
  orderStatus,
  outstandingCents,
  method,
}: {
  paymentRecordStatus: NormalizedPaymentRecordStatus;
  orderStatus: NormalizedOrderPaymentStatus;
  outstandingCents: number;
  method: 'cash' | 'card' | 'unknown';
}): TablePaymentStatus => {
  const hasOutstanding = outstandingCents > 0;

  if (hasOutstanding) {
    if (paymentRecordStatus === 'failed' || orderStatus === 'failed') {
      return 'failed';
    }
    if (method === 'cash') {
      return 'cash';
    }
    return 'pending';
  }

  if (paymentRecordStatus === 'failed' || orderStatus === 'failed') {
    return 'failed';
  }

  if (paymentRecordStatus === 'pending' || orderStatus === 'pending') {
    return 'pending';
  }

  if (paymentRecordStatus === 'paid' || orderStatus === 'paid') {
    return 'paid';
  }

  return 'none';
};

type RelativeFormatter = (value: Date | string | null | undefined) => string;

const RelativeTimeLabel = React.memo<{
  timestamp: Date | null;
  formatter: RelativeFormatter;
  fallback: string;
}>(({ timestamp, formatter, fallback }) => {
  const labelRef = useRef<HTMLSpanElement | null>(null);
  const baseTime = timestamp?.getTime() ?? null;

  const computeLabel = useCallback(() => {
    if (!baseTime) {
      return fallback;
    }

    return formatter(new Date(baseTime));
  }, [baseTime, fallback, formatter]);

  useEffect(() => {
    if (!labelRef.current) {
      return;
    }
    labelRef.current.textContent = computeLabel();
  }, [computeLabel]);

  useEffect(() => {
    if (!baseTime || typeof window === "undefined") {
      return;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;

    const update = () => {
      if (!labelRef.current) {
        return;
      }
      const next = computeLabel();
      if (labelRef.current.textContent !== next) {
        labelRef.current.textContent = next;
      }
      timer = window.setTimeout(update, 1000);
    };

    timer = window.setTimeout(update, 1000);

    return () => {
      if (timer) {
        window.clearTimeout(timer);
      }
    };
  }, [computeLabel, baseTime]);

  return <span ref={labelRef}>{computeLabel()}</span>;
});

type OrderRecord = {
  id: string;
  code: string;
  total_cents: number;
  status: OrderStatus;
  created_at: string;
  table_id: string | null;
  source: "QR" | "COUNTER" | null;
  order_items: Array<{ id: string; name: string; quantity: number }>;
  station_snapshot: StationSnapshot | null;
  payment_status?: string | null;
  payment_method?: string | null;
  collected_cents?: number;
  outstanding_cents?: number;
  currency?: string | null;
};

const mapOrderSummaryToRecord = (order: OrderSummary): OrderRecord => {
  const fallbackCode = order.id.slice(0, 8).toUpperCase();
  const totalCents =
    order.totals?.totalCents ??
    (typeof order.totalCents === "number" ? order.totalCents : 0);
  const createdAt = order.createdAt ?? order.placedAt ?? new Date().toISOString();
  const sourceValue = typeof order.source === "string" ? order.source.toUpperCase() : null;
  const source: OrderRecord["source"] =
    sourceValue === "QR"
      ? "QR"
      : sourceValue === "COUNTER"
      ? "COUNTER"
      : null;

  const items = (order.items ?? []).map((item, index) => ({
    id: item.id ?? `${order.id}:${index}`,
    name: item.name ?? "Item",
    quantity: typeof item.quantity === "number" && Number.isFinite(item.quantity) ? item.quantity : 1,
  }));

  return {
    id: order.id,
    code: order.code ?? fallbackCode,
    total_cents: totalCents,
    status: order.status,
    created_at: createdAt,
    table_id: order.tableId ?? null,
    source,
    order_items: items,
    station_snapshot: order.stationSnapshot ?? null,
    payment_status: order.paymentStatus ?? null,
    payment_method: order.paymentMethod ?? order.payment?.method ?? null,
    collected_cents: order.collectedCents ?? 0,
    outstanding_cents:
      typeof order.outstandingBalanceCents === "number"
        ? order.outstandingBalanceCents
        : Math.max(0, totalCents - (order.collectedCents ?? 0)),
    currency: order.currency ?? order.totals?.currency ?? null,
  };
};

type DashboardDerivedData = {
  metrics: DashboardMetric[];
  recentOrders: RecentOrderRow[];
  tables: TableSummary[];
  liveStatus: LiveStatusData;
  performance: PerformanceMetrics;
  salesSeries: SalesPoint[];
  hourlySeries: HourlyPoint[];
  topItems: TopItemRow[];
  notifications: number;
};

type DashboardDataHelpers = {
  t: (key: string, options?: Record<string, unknown>) => string;
  formatRelative: (value: Date | string | null | undefined) => string;
  formatCurrency: (value: number, currency?: string, options?: { isMinorUnit?: boolean }) => string;
  formatNumber: (value: number) => string;
  currency: string;
  locationOrderingById: Map<string, TenantLocationRecord['qrOrderingStatus']>;
  locationOrderingByName: Map<string, TenantLocationRecord['qrOrderingStatus']>;
};

type AlertEntry = {
  id: string;
  code: string;
  totalLabel: string;
  timeLabel: string;
  tableLabel: string;
  status: string;
  statusLabel: string;
  source: "QR" | "COUNTER" | null;
  sourceLabel: string;
  itemsSummary: string | null;
};

const normaliseHistory = (values: number[], targetLength = 7) => {
  const padded = Array.from({ length: targetLength }, (_, index) => values[index] ?? 0);
  const max = Math.max(...padded, 0);
  if (max === 0) {
    return padded.map(() => 0);
  }
  return padded.map((value) => Math.round((value / max) * 100));
};

const buildHourlySeries = (orders: OrderRecord[]): HourlyPoint[] => {
  const buckets = new Map<number, number>();
  orders.forEach((order) => {
    const date = new Date(order.created_at);
    const hour = date.getHours();
    buckets.set(hour, (buckets.get(hour) ?? 0) + 1);
  });
  const sorted = Array.from(buckets.entries()).sort((a, b) => a[0] - b[0]);
  return sorted.map(([hour, value]) => ({
    hour: hourFormatter.format(new Date().setHours(hour, 0, 0, 0)),
    orders: value,
  }));
};

const deriveDashboardData = (
  orders: OrderRecord[],
  tables: RestaurantTable[],
  tableActivity: TableActivitySummaryResponse | undefined,
  payments: ApiPaymentRecord[],
  salesReport: SalesReportResponse | undefined,
  itemsReport: ItemsReportResponse | undefined,
  staff: StaffMember[],
  qrMetrics: QrMetricsResponse | null | undefined,
  helpers: DashboardDataHelpers,
): DashboardDerivedData => {
  const {
    t,
    formatRelative,
    formatCurrency: formatCurrencyLabel,
    formatNumber,
    currency,
    locationOrderingById,
    locationOrderingByName,
  } = helpers;

  const now = new Date();
  const sortedOrders = [...orders].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  const totalsToday = sortedOrders.filter((order) => startOfDay(new Date(order.created_at)).getTime() === startOfDay(now).getTime());

  const series = [...(salesReport?.series ?? [])].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const currentWeekSeries = series.slice(-7);
  const previousWeekSeries = series.slice(-14, -7);

  const currentWeekRevenue = currentWeekSeries.reduce((sum, day) => sum + day.revenueCents, 0);
  const previousWeekRevenue = previousWeekSeries.reduce((sum, day) => sum + day.revenueCents, 0);
  const revenueDelta = calculatePercentChange(currentWeekRevenue, previousWeekRevenue);

  const currentWeekOrders = currentWeekSeries.reduce((sum, day) => sum + day.totalOrders, 0);
  const previousWeekOrders = previousWeekSeries.reduce((sum, day) => sum + day.totalOrders, 0);
  const ordersDelta = calculatePercentChange(currentWeekOrders, previousWeekOrders);

  const averageOrderCurrent = salesReport?.summary.averageOrderCents ?? 0;
  const averageOrderPrevious = previousWeekOrders > 0 ? Math.round(previousWeekRevenue / previousWeekOrders) : 0;
  const averageDelta = calculatePercentChange(averageOrderCurrent, averageOrderPrevious);

  const revenueHistory = normaliseHistory(currentWeekSeries.map((day) => day.revenueCents));
  const ordersHistory = normaliseHistory(currentWeekSeries.map((day) => day.totalOrders));
  const averageHistory = normaliseHistory(currentWeekSeries.map((day) => day.averageOrderCents ?? averageOrderCurrent));

  const activityTotals = tableActivity?.totals ?? null;
  const reservedTableIds = new Set(tableActivity?.reservedTableIds ?? []);
  const activityHistory = tableActivity?.history?.map((point) => point.activeCount) ?? null;

  const tableOrdersMap = new Map<string, OrderRecord[]>();
  sortedOrders.forEach((order) => {
    if (!order.table_id) {
      return;
    }
    const existing = tableOrdersMap.get(order.table_id) ?? [];
    tableOrdersMap.set(order.table_id, [...existing, order]);
  });

  const qrMetricsMap = new Map<string, QrMetricsResponse["tables"][number]>();
  (qrMetrics?.tables ?? []).forEach((entry) => {
    if (entry && entry.tableId) {
      qrMetricsMap.set(entry.tableId, entry);
    }
  });

  const latestPaymentByOrderId = new Map<string, ApiPaymentRecord>();
  payments.forEach((record) => {
    if (!record.orderId || latestPaymentByOrderId.has(record.orderId)) {
      return;
    }
    latestPaymentByOrderId.set(record.orderId, record);
  });

  const { orderedTables: queueOrderedTables } = buildTableQueueOrdering(tables);

  const tableSummariesUnordered: TableSummary[] = tables.map((table) => {
    const ordersForTable = tableOrdersMap.get(table.id) ?? [];
    const latestOrder = ordersForTable[0];
    const activeOrder = ordersForTable.find((order) => WAITING_STATUSES.has(order.status));
    const revenueFromOrders = ordersForTable.reduce((sum, order) => sum + order.total_cents, 0);
    const metricsForTable = qrMetricsMap.get(table.id);
    const revenueCents = metricsForTable?.revenueCents ?? revenueFromOrders;
    const payment = activeOrder ? latestPaymentByOrderId.get(activeOrder.id) : undefined;

    const ordersCount = metricsForTable?.ordersCount ?? ordersForTable.length;
    const scansCount = metricsForTable?.scansCount ?? ordersForTable.length;
    const hasActiveOrder = Boolean(activeOrder);
    const isReserved = reservedTableIds.has(table.id) || table.status === "RESERVED";
    let status: TableSummary["status"] = "idle";
    const normalizedLocationName = table.location ? table.location.trim().toLowerCase() : null;
    const orderingStatus =
      (table.locationId ? locationOrderingById.get(table.locationId) : undefined) ??
      (normalizedLocationName ? locationOrderingByName.get(normalizedLocationName) : undefined) ??
      null;

    if (hasActiveOrder) {
      status = "active";
    } else if (isReserved) {
      status = "reserved";
    }

    if (status === "idle") {
      if (orderingStatus === "paused") {
        status = "paused";
      } else if (orderingStatus === "stopped") {
        status = "stopped";
      }
    }

    const activeOrderForDisplay = hasActiveOrder ? activeOrder : undefined;
    const outstandingSource =
      activeOrderForDisplay?.outstanding_cents ??
      (activeOrderForDisplay ? activeOrderForDisplay.total_cents - (activeOrderForDisplay.collected_cents ?? 0) : 0);
    const outstandingCents = Math.max(0, Math.round(outstandingSource ?? 0));
    const normalizedMethod = activeOrderForDisplay
      ? normalizePaymentMethod(activeOrderForDisplay.payment_method ?? payment?.method ?? null)
      : "unknown";
    const normalizedOrderPaymentStatus = normalizeOrderPaymentStatus(activeOrderForDisplay?.payment_status);
    const paymentRecordStatus = normalizePaymentRecordStatus(payment);
    const tablePaymentStatus: TableSummary["paymentStatus"] = activeOrderForDisplay
      ? resolveTablePaymentStatus({
          paymentRecordStatus,
          orderStatus: normalizedOrderPaymentStatus,
          outstandingCents,
          method: normalizedMethod,
        })
      : "none";

    const statusLabel = t(`dashboard:tables.status.${status}`, { defaultValue: formatStatusLabel(status) });

    const lastActivitySource =
      metricsForTable?.lastActivityAt ?? (latestOrder ? latestOrder.created_at : table.lastOrderAt);

    return {
      id: table.id,
      label:
        table.name ??
        (table.tableNumber ? t('dashboard:orders.tablePrefix', { number: table.tableNumber }) : t('dashboard:orders.counterLabel')),
      status,
      statusLabel,
      scans: scansCount,
      lastActivity: lastActivitySource ? formatRelative(lastActivitySource) : t('dashboard:orders.noActivity'),
      revenueLabel: formatCurrencyLabel(revenueCents, currency, { isMinorUnit: true }),
      ordersCount,
      paymentStatus: tablePaymentStatus,
      currentOrder: activeOrderForDisplay
        ? {
            id: activeOrderForDisplay.code,
            orderId: activeOrderForDisplay.id,
            amountCents: outstandingCents,
            amountLabel: formatCurrencyLabel(
              outstandingCents > 0 ? outstandingCents : activeOrderForDisplay.total_cents,
              currency,
              { isMinorUnit: true },
            ),
            method: normalizedMethod,
            statusLabel: (() => {
              const status = (payment?.status ?? activeOrderForDisplay.status ?? 'PENDING').toLowerCase();
              return t(`dashboard:orders.status.${status}`, { defaultValue: formatStatusLabel(status) });
            })(),
            paymentStatus: tablePaymentStatus,
          }
        : undefined,
    };
  });

  const tableSummaryById = new Map(tableSummariesUnordered.map((summary) => [summary.id, summary]));
  const tableSummaries: TableSummary[] = queueOrderedTables
    .map((table) => tableSummaryById.get(table.id))
    .filter((summary): summary is TableSummary => Boolean(summary));

  const inferredTotalTables = tables.length;
  const totalTables = Math.max(activityTotals?.total ?? inferredTotalTables, 1);
  const activeTables =
    activityTotals?.active ?? tableSummaries.filter((table) => table.status === "active").length;
  const reservedTables =
    activityTotals?.reserved ?? tableSummaries.filter((table) => table.status === "reserved").length;
  const availableTables = Math.max(totalTables - activeTables - reservedTables, 0);

  const waitingOrders = sortedOrders.filter((order) => WAITING_STATUSES.has(order.status)).length;
  const completedOrders = sortedOrders.filter((order) => COMPLETED_STATUSES.has(order.status)).length;

  const averagePrepMinutes =
    sortedOrders.length === 0
      ? 0
      : Math.max(
          0,
          Math.round(
            sortedOrders.reduce((sum, order) => sum + (now.getTime() - new Date(order.created_at).getTime()) / 60000, 0) /
              sortedOrders.length,
          ),
        );

  const hourlySeries = buildHourlySeries(totalsToday);
  const peakHour = hourlySeries.reduce<HourlyPoint | null>((acc, point) => {
    if (!acc || point.orders > acc.orders) {
      return point;
    }
    return acc;
  }, null);

  const topItems: TopItemRow[] = (itemsReport?.rows ?? []).map((row) => ({
    id: row.itemId,
    name: row.name,
    quantity: row.quantity,
    revenue: row.revenueCents,
    revenueLabel: formatCurrencyLabel(row.revenueCents, currency, { isMinorUnit: true }),
    percentage: 0,
    trend: "neutral",
    trendLabel: "0%",
  }));

  const topRevenue = Math.max(...topItems.map((item) => item.revenue), 0);
  if (topRevenue > 0) {
    topItems.forEach((item) => {
      item.percentage = clampPercent((item.revenue / topRevenue) * 100);
    });
  }

  const revenueDeltaLabel = formatDelta(revenueDelta);
  const ordersDeltaLabel = formatDelta(ordersDelta);
  const averageDeltaLabel = formatDelta(averageDelta);

  const activeHistorySource =
    activityHistory && activityHistory.length > 0
      ? activityHistory.slice(-7)
      : tableSummaries.map((table) => table.ordersCount).slice(0, 7);
  const activeHistoryNormalized = normaliseHistory(activeHistorySource);
  const latestActiveSample = activeHistorySource[activeHistorySource.length - 1] ?? activeTables;
  const previousActiveSample =
    activeHistorySource.length > 1 ? activeHistorySource[activeHistorySource.length - 2] ?? 0 : 0;
  const occupancyDelta = calculatePercentChange(latestActiveSample, previousActiveSample);
  const occupancyDeltaLabel = formatDelta(occupancyDelta);

  const metrics: DashboardMetric[] = [
    {
      id: "revenue",
      label: t('dashboard:metrics.cards.revenue.label'),
      value: formatCurrencyLabel(currentWeekRevenue, currency, { isMinorUnit: true }),
      changeLabel: t('dashboard:metrics.cards.revenue.change', { delta: revenueDeltaLabel }),
      trend: determineTrend(revenueDelta),
      description: t('dashboard:metrics.cards.revenue.description'),
      history: revenueHistory,
      icon: <DollarSign className="h-4 w-4" />,
    },
    {
      id: "orders",
      label: t('dashboard:metrics.cards.orders.label'),
      value: formatNumber(currentWeekOrders),
      changeLabel: t('dashboard:metrics.cards.orders.change', { delta: ordersDeltaLabel }),
      trend: determineTrend(ordersDelta),
      description: t('dashboard:metrics.cards.orders.description'),
      history: ordersHistory,
      icon: <ShoppingBag className="h-4 w-4" />,
    },
    {
      id: "average-order",
      label: t('dashboard:metrics.cards.averageTicket.label'),
      value: formatCurrencyLabel(averageOrderCurrent, currency, { isMinorUnit: true }),
      changeLabel: t('dashboard:metrics.cards.averageTicket.change', { delta: averageDeltaLabel }),
      trend: determineTrend(averageDelta),
      description: t('dashboard:metrics.cards.averageTicket.description'),
      history: averageHistory,
      icon: <BarChart3 className="h-4 w-4" />,
    },
    {
      id: "active-tables",
      label: t('dashboard:metrics.cards.activeTables.label'),
      value: `${activeTables}/${totalTables}`,
      changeLabel: t('dashboard:metrics.cards.activeTables.change', { delta: occupancyDeltaLabel }),
      trend: determineTrend(occupancyDelta),
      description: t('dashboard:metrics.cards.activeTables.description'),
      history: activeHistoryNormalized,
      icon: <QrCode className="h-4 w-4" />,
    },
  ];

  const liveStatus: LiveStatusData = {
    occupancy: {
      occupiedPercent: clampPercent((activeTables / totalTables) * 100),
      reservedPercent: clampPercent((reservedTables / totalTables) * 100),
      availablePercent: clampPercent((availableTables / totalTables) * 100),
    },
    waitingOrders,
    completedOrders,
    avgPrepTimeLabel:
      averagePrepMinutes > 0
        ? t('dashboard:liveStatus.avgPrepTimeMinutes', { value: averagePrepMinutes })
        : t('dashboard:liveStatus.avgPrepTimeLessThanMinute'),
    peakHourLabel: peakHour ? `${peakHour.hour}` : "--",
    staffOnline: staff.filter((member) => member.status !== "inactive").length,
  };

  const averageOrdersPerTable = (sortedOrders.length / totalTables).toFixed(1);

  const performance: PerformanceMetrics = {
    successRateLabel: sortedOrders.length === 0 ? '100%' : `${Math.round((completedOrders / sortedOrders.length) * 100)}%`,
    successDeltaLabel: t('dashboard:serviceInsights.successRate.delta', { delta: formatDelta(ordersDelta) }),
    avgScanTimeLabel: t('dashboard:serviceInsights.avgThroughput.value', { value: averageOrdersPerTable }),
    scanDeltaLabel: t('dashboard:serviceInsights.avgThroughput.delta', { delta: formatDelta(revenueDelta) }),
    revenueLabel: formatCurrencyLabel(currentWeekRevenue, currency, { isMinorUnit: true }),
    revenueDeltaLabel: t('dashboard:serviceInsights.revenueFocus.delta', { delta: formatDelta(revenueDelta) }),
  };

  const weeklySeries: SalesPoint[] = currentWeekSeries.map((day) => ({
    day: dayFormatter.format(new Date(day.date)),
    revenue: day.revenueCents,
    orders: day.totalOrders,
  }));

  return {
    metrics,
    recentOrders: sortedOrders.slice(0, 8).map<RecentOrderRow>((order) => {
      const itemsSummary = order.order_items.map((item) => `${item.quantity}x ${item.name}`).join(", ") || undefined;
      const stationEntries = order.station_snapshot
        ? Object.values(order.station_snapshot.stations ?? {}).sort((a, b) => {
            if (b.projectedOrdersPerMinute !== a.projectedOrdersPerMinute) {
              return b.projectedOrdersPerMinute - a.projectedOrdersPerMinute;
            }
            return b.projectedItemsPerMinute - a.projectedItemsPerMinute;
          })
        : [];
      const stationLoad =
        stationEntries.length > 0
          ? stationEntries.slice(0, 2).map((station) => {
              const ordersPerMinute = Math.round(station.projectedOrdersPerMinute * 10) / 10;
              const itemsPerMinute = Math.round(station.projectedItemsPerMinute * 10) / 10;
              const limit = station.throttleIsHardStop
                ? 0
                : station.throttleMaxOrdersPerMinute ?? station.maxOrdersPerMinute ?? null;
              const ordersLabel =
                limit !== null && limit >= 0
                  ? t("kitchen:labels.ordersPerMinute", {
                      defaultValue: "{{orders}} / {{limit}} orders/min",
                      orders: ordersPerMinute,
                      limit,
                    })
                  : t("kitchen:labels.ordersPerMinuteUnlimited", {
                      defaultValue: "{{orders}} orders/min",
                      orders: ordersPerMinute,
                    });
              const itemsLabel = t("kitchen:labels.itemsPerMinute", {
                defaultValue: "{{items}} items/min",
                items: itemsPerMinute,
              });
              const flagged =
                station.throttleIsHardStop ||
                (limit !== null && limit >= 0 && ordersPerMinute > limit);
              return {
                id: station.stationId,
                label: station.name ?? station.code ?? t("kitchen:labels.stationFallback", { defaultValue: "Station" }),
                ordersLabel,
                itemsLabel,
                flagged,
                throttleEndsAt: station.throttleEndsAt ?? null,
                overrideApplied: station.overrideApplied ?? false,
              };
            })
          : undefined;

      return {
        id: order.id,
        code: order.code,
        itemsSummary,
        tableLabel:
          tables.find((table) => table.id === order.table_id)?.name ??
          (order.table_id ? t('dashboard:orders.tablePrefix', { number: order.table_id }) : t('dashboard:orders.counterLabel')),
        orderType: order.source === "QR" ? "qr" : "counter",
        status: order.status,
        statusTone: determineTrend(
          COMPLETED_STATUSES.has(order.status) ? 5 : WAITING_STATUSES.has(order.status) ? 0 : -5,
        ),
        totalLabel: formatCurrencyLabel(order.total_cents, currency, { isMinorUnit: true }),
        timeLabel: formatRelative(order.created_at),
        stationLoad,
        throttleOverrideApplied: Boolean(order.station_snapshot?.overrideApplied),
      };
    }),
    tables: tableSummaries,
    liveStatus,
    performance,
    salesSeries: weeklySeries,
    hourlySeries,
    topItems,
    notifications: waitingOrders,
  };
};
const DashboardSkeleton: React.FC = () => {
  const { t } = useTranslation(['common', 'dashboard']);

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/20">
      <div className="rounded-xl border border-border bg-background px-6 py-10 text-center shadow-lg">
        <p className="text-lg font-semibold text-muted-foreground">{t('dashboard:fallback.loadingMessage')}</p>
      </div>
    </div>
  );
};

type AlertsPopoverContentProps = {
  alerts: AlertEntry[];
  onViewOrder: (orderId: string) => void;
  onViewAllOrders: () => void;
  onClearAll: () => void;
  hasAnyAlerts: boolean;
};

const AlertsPopoverContent: React.FC<AlertsPopoverContentProps> = ({
  alerts,
  onViewOrder,
  onViewAllOrders,
  onClearAll,
  hasAnyAlerts,
}) => {
  const { t } = useTranslation(["common", "dashboard", "orders"]);
  const hasAlerts = alerts.length > 0;

  return (
    <div className="w-full">
      <div className="border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("dashboard:alerts.title")}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">{t("dashboard:alerts.subtitle")}</p>
          </div>
          {hasAnyAlerts ? (
            <Button
              onClick={onClearAll}
              variant="link"
              size="sm"
              className="h-auto px-0 py-0 text-xs font-semibold text-primary hover:text-primary/80"
            >
              {t("dashboard:alerts.clearAll", { defaultValue: "Clear all" })}
            </Button>
          ) : null}
        </div>
      </div>
      <ScrollArea className="max-h-80 px-4 py-3">
        <div className="space-y-6">
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t("dashboard:alerts.newOrders")}
              </span>
              {hasAlerts ? (
                <Badge variant="secondary" className="rounded-full px-2 py-0 text-xs">
                  {alerts.length}
                </Badge>
              ) : null}
            </div>
            <div className="mt-3 space-y-3">
              {hasAlerts ? (
                alerts.map((alert) => (
                  <button
                    key={alert.id}
                    type="button"
                    onClick={() => onViewOrder(alert.id)}
                    className="w-full rounded-lg border border-border bg-card p-4 text-left transition hover:border-primary/50 hover:bg-muted"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{alert.code}</p>
                        <p className="text-xs text-muted-foreground">
                          {alert.tableLabel} - {alert.sourceLabel} - {alert.timeLabel}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {alert.totalLabel}
                        </p>
                        <Badge variant="outline" className="mt-1 text-[11px] capitalize">
                          {alert.statusLabel}
                        </Badge>
                      </div>
                    </div>
                    {alert.itemsSummary ? (
                      <p className="mt-2 text-xs text-muted-foreground">{alert.itemsSummary}</p>
                    ) : null}
                  </button>
                ))
              ) : (
                <div className="rounded-lg border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground">
                  {t("dashboard:alerts.empty")}
                </div>
              )}
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("dashboard:alerts.integrations.title")}
            </span>
            <p className="text-sm text-muted-foreground">
              {t("dashboard:alerts.integrations.description")}
            </p>
          </div>
        </div>
      </ScrollArea>
      <div className="border-t border-border bg-background/90 px-4 py-3">
        <Button onClick={onViewAllOrders} className="w-full" variant="outline">
          {t("dashboard:alerts.viewAll")}
        </Button>
      </div>
    </div>
  );
};

const RealDashboardPageV3: React.FC = () => {
  const { t } = useTranslation(["common", "dashboard", "orders"]);
  const { language, formatCurrency: localeFormatCurrency, formatNumber, formatRelativeTime: localeRelativeTime } = useLocaleFormatter();
  const { user, loading: authLoading } = useAuth();
  const { staffInfo, loading: roleLoading } = useRole();
  const { restaurantData, loading: tenantLoading } = useTenant();
  const { subdomain: routeSubdomain } = useParams<{ subdomain?: string }>();
  const { tenantPath } = useTenantPath();
  const storeTenantSlug = useTenantSlug();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { settings: restaurantSettings, preferences: restaurantPreferences } = useRestaurantSettings();
  const dashboardCurrency = useMemo(
    () => (restaurantSettings?.currency ?? "USD").toUpperCase(),
    [restaurantSettings?.currency],
  );
  const autoRefreshEnabled = restaurantPreferences?.autoRefresh ?? true;
  const tenantSlug = useMemo(
    () => restaurantData?.subdomain ?? storeTenantSlug ?? routeSubdomain ?? null,
    [restaurantData?.subdomain, routeSubdomain, storeTenantSlug],
  );
  
  // Enable real-time order event listening
  useOrderEvents();

  const locationsQuery = useQuery({
    queryKey: ['settings', 'locations'],
    queryFn: ({ signal }) => fetchTenantLocations(signal).then((response) => response.locations),
    enabled: Boolean(restaurantData),
    staleTime: 60_000,
  });

  const locationOrderingById = useMemo(() => {
    const map = new Map<string, TenantLocationRecord['qrOrderingStatus']>();
    (locationsQuery.data ?? []).forEach((location) => {
      if (location.id) {
        map.set(location.id, location.qrOrderingStatus);
      }
    });
    return map;
  }, [locationsQuery.data]);

  const locationOrderingByName = useMemo(() => {
    const map = new Map<string, TenantLocationRecord['qrOrderingStatus']>();
    (locationsQuery.data ?? []).forEach((location) => {
      const normalized = location.name.trim().toLowerCase();
      if (normalized) {
        map.set(normalized, location.qrOrderingStatus);
      }
    });
    return map;
  }, [locationsQuery.data]);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isBootstrapped, setIsBootstrapped] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [isAlertPeekOpen, setIsAlertPeekOpen] = useState(false);
  const [alertOrderId, setAlertOrderId] = useState<string | null>(null);
  const [isAlertActionPending, setIsAlertActionPending] = useState(false);
  const [acknowledgedAlertIds, setAcknowledgedAlertIds] = useState<Set<string>>(() => new Set());
  const [isCounterPeekOpen, setIsCounterPeekOpen] = useState(false);
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false);
  const [isQrOrderingPopoverOpen, setIsQrOrderingPopoverOpen] = useState(false);
  const [counterPeekKey, setCounterPeekKey] = useState(0);
  const [cashSettlementOrderId, setCashSettlementOrderId] = useState<string | null>(null);
  const counterPeekUrl = useMemo(() => tenantPath("sales/new/peek?source=counter&view=peek"), [tenantPath]);
  
  // Track shown errors to prevent re-showing on language changes
  const shownErrorsRef = useRef<Set<string>>(new Set());
  
  // Memoized error messages to avoid translation dependency issues
  const errorMessages = useMemo(() => ({
    ordersLoad: t("dashboard:toasts.errors.ordersLoad"),
    tablesLoad: t("dashboard:toasts.errors.tablesLoad"),
    paymentsLoad: t("dashboard:toasts.errors.paymentsLoad"),
    salesReportLoad: t("dashboard:toasts.errors.salesReportLoad"),
  }), [t]);
  
  // Helper function to show error only once per error instance
  const showErrorOnce = useCallback((errorKey: string, error: unknown, message: string) => {
    if (isIgnorableQueryError(error)) {
      return;
    }

    const errorId = `${errorKey}-${error instanceof Error ? error.message : String(error)}`;
    
    if (shownErrorsRef.current.has(errorId)) {
      return; // Already shown this error
    }
    
    shownErrorsRef.current.add(errorId);
    const log = isUnauthenticatedError(error) ? console.debug : console.error;
    log(`Failed to load ${errorKey}`, error);
    toast({ title: message, variant: "destructive" });
  }, [toast]);

  const formatRelative = React.useCallback(
    (value: Date | string | null | undefined) => {
      if (!value) {
        return t('common:dates.justNow');
      }
      return localeRelativeTime(value);
    },
    [localeRelativeTime, t],
  );

  // Reset bootstrap guard when tenant context changes so we re-show the skeleton for new data.
  useEffect(() => {
    setIsBootstrapped(false);
  }, [tenantSlug]);
  const today = new Date();
  const toIso = endOfDay(today).toISOString();
  const fromIso = startOfDay(subtractDays(today, 13)).toISOString();
  const reportFromDate = isoDate(subtractDays(today, 13));
  const reportToDate = isoDate(today);

  const ordersQuery = useQuery<OrderRecord[]>({
    queryKey: [...DASHBOARD_QUERY_ROOT, "orders", tenantSlug, fromIso, toIso],
    enabled: Boolean(tenantSlug),
    refetchInterval: autoRefreshEnabled ? 10_000 : false,
    queryFn: async ({ signal }) => {
      if (!tenantSlug) {
        return [];
      }
      try {
        const response = await fetchOrders(
          { limit: DASHBOARD_ORDERS_LIMIT, sort: "desc" },
          signal,
        );
        return response.orders.map(mapOrderSummaryToRecord);
      } catch (error) {
        if (isIgnorableQueryError(error)) {
          throw new CancelledError({ silent: true });
        }
        const log = isUnauthenticatedError(error) ? console.debug : console.error;
        log("[dashboard] orders fetch failed", error);
        throw error;
      }
    },
  });

  const tablesQuery = useQuery({
    queryKey: [...DASHBOARD_QUERY_ROOT, "tables", tenantSlug],
    enabled: Boolean(tenantSlug),
    refetchInterval: autoRefreshEnabled ? 15_000 : false,
    queryFn: ({ signal }) => (tenantSlug ? listTables(tenantSlug, signal) : Promise.resolve({ tables: [] })),
  });

  const tableActivityQuery = useQuery({
    queryKey: [...DASHBOARD_QUERY_ROOT, "table-activity", tenantSlug],
    enabled: Boolean(tenantSlug),
    refetchInterval: autoRefreshEnabled ? 15_000 : false,
    queryFn: ({ signal }) =>
      tenantSlug ? fetchTableActivitySummary(tenantSlug, signal) : Promise.resolve(undefined),
  });

  const paymentsQuery = useQuery({
    queryKey: [...DASHBOARD_QUERY_ROOT, "payments", reportFromDate, reportToDate],
    enabled: Boolean(tenantSlug),
    refetchInterval: autoRefreshEnabled ? 60_000 : false,
    queryFn: ({ signal }) =>
      fetchPayments({ from: reportFromDate, to: reportToDate, limit: 100 }, signal).then((result) =>
        result.payments ?? result.data ?? [],
      ),
  });

  const salesReportQuery = useQuery({
    queryKey: [...DASHBOARD_QUERY_ROOT, "sales", reportFromDate, reportToDate],
    enabled: Boolean(tenantSlug),
    refetchInterval: autoRefreshEnabled ? 60_000 : false,
    queryFn: ({ signal }) => fetchSalesReport({ from: reportFromDate, to: reportToDate }, signal),
  });

  const itemsReportQuery = useQuery({
    queryKey: [...DASHBOARD_QUERY_ROOT, "items", reportFromDate, reportToDate],
    enabled: Boolean(tenantSlug),
    refetchInterval: autoRefreshEnabled ? 120_000 : false,
    queryFn: ({ signal }) => fetchItemsReport({ from: reportFromDate, to: reportToDate }, signal),
  });

  const staffQuery = useQuery({
    queryKey: [...DASHBOARD_QUERY_ROOT, "staff"],
    enabled: Boolean(tenantSlug),
    queryFn: ({ signal }) => fetchStaff({ signal }),
  });

  const qrMetricsQuery = useQuery({
    queryKey: [...DASHBOARD_QUERY_ROOT, "qr-metrics", tenantSlug],
    enabled: Boolean(tenantSlug),
    refetchInterval: autoRefreshEnabled ? 30_000 : false,
    queryFn: ({ signal }) =>
      tenantSlug ? fetchQrMetrics(tenantSlug, { signal }) : Promise.resolve(null),
  });

  // Handle query errors
  useEffect(() => {
    if (ordersQuery.error && !isIgnorableQueryError(ordersQuery.error)) {
      showErrorOnce("orders", ordersQuery.error, errorMessages.ordersLoad);
    }
  }, [ordersQuery.error, showErrorOnce, errorMessages.ordersLoad]);

  useEffect(() => {
    if (tablesQuery.error && !isIgnorableQueryError(tablesQuery.error)) {
      showErrorOnce("tables", tablesQuery.error, errorMessages.tablesLoad);
    }
  }, [tablesQuery.error, showErrorOnce, errorMessages.tablesLoad]);

  useEffect(() => {
    if (tableActivityQuery.error && !isIgnorableQueryError(tableActivityQuery.error)) {
      const log = isUnauthenticatedError(tableActivityQuery.error) ? console.debug : console.error;
      log("Failed to load table activity summary", tableActivityQuery.error);
    }
  }, [tableActivityQuery.error]);

  useEffect(() => {
    if (paymentsQuery.error && !isIgnorableQueryError(paymentsQuery.error)) {
      showErrorOnce("payments", paymentsQuery.error, errorMessages.paymentsLoad);
    }
  }, [paymentsQuery.error, showErrorOnce, errorMessages.paymentsLoad]);

  useEffect(() => {
    if (salesReportQuery.error && !isIgnorableQueryError(salesReportQuery.error)) {
      showErrorOnce("salesReport", salesReportQuery.error, errorMessages.salesReportLoad);
    }
  }, [salesReportQuery.error, showErrorOnce, errorMessages.salesReportLoad]);

  useEffect(() => {
    if (itemsReportQuery.error && !isIgnorableQueryError(itemsReportQuery.error)) {
      const log = isUnauthenticatedError(itemsReportQuery.error) ? console.debug : console.error;
      log("Failed to load menu analytics", itemsReportQuery.error);
      // Note: No toast for items report as it's not critical for dashboard functionality
    }
  }, [itemsReportQuery.error]);

  useEffect(() => {
    if (staffQuery.error && !isIgnorableQueryError(staffQuery.error)) {
      const log = isUnauthenticatedError(staffQuery.error) ? console.debug : console.error;
      log("Failed to load staff", staffQuery.error);
      // Note: No toast for staff as it's not critical for dashboard functionality
    }
  }, [staffQuery.error]);

  useEffect(() => {
    if (qrMetricsQuery.error && !isIgnorableQueryError(qrMetricsQuery.error)) {
      const log = isUnauthenticatedError(qrMetricsQuery.error) ? console.debug : console.error;
      log("Failed to load QR metrics", qrMetricsQuery.error);
    }
  }, [qrMetricsQuery.error]);

  const derivedData = useMemo(() => {
    return deriveDashboardData(
      (ordersQuery.data as OrderRecord[]) ?? [],
      (tablesQuery.data as { tables: RestaurantTable[] })?.tables ?? [],
      tableActivityQuery.data as TableActivitySummaryResponse | undefined,
      (paymentsQuery.data as ApiPaymentRecord[]) ?? [],
      salesReportQuery.data,
      itemsReportQuery.data,
      (staffQuery.data as { staff: StaffMember[] })?.staff ?? [],
      qrMetricsQuery.data as QrMetricsResponse | null | undefined,
      {
        t: t as (key: string, options?: Record<string, unknown>) => string,
        formatRelative,
        formatCurrency: (value: number, currency = dashboardCurrency, options?: { isMinorUnit?: boolean }) =>
          localeFormatCurrency(value, currency, options),
        formatNumber,
        currency: dashboardCurrency,
        locationOrderingById,
        locationOrderingByName,
      },
    );
  }, [
    ordersQuery.data,
    tablesQuery.data,
    tableActivityQuery.data,
    paymentsQuery.data,
    salesReportQuery.data,
    itemsReportQuery.data,
    staffQuery.data,
    qrMetricsQuery.data,
    t,
    formatRelative,
    localeFormatCurrency,
    dashboardCurrency,
    formatNumber,
    locationOrderingById,
    locationOrderingByName,
  ]);

  const tableLookup = useMemo(() => {
    const map = new Map<string, RestaurantTable>();
    ((tablesQuery.data as { tables: RestaurantTable[] })?.tables ?? []).forEach((table) => {
      if (table?.id) {
        map.set(table.id, table);
      }
    });
    return map;
  }, [tablesQuery.data]);

  const alertEntries = useMemo<AlertEntry[]>(() => {
    return ((ordersQuery.data as OrderRecord[]) ?? [])
      .filter((order) => WAITING_STATUSES.has(order.status))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .map((order) => {
        const table = order.table_id ? tableLookup.get(order.table_id) : undefined;
        const tableLabel =
          table?.name ??
          (order.table_id ? t('dashboard:orders.tablePrefix', { defaultValue: 'Table {{number}}', number: order.table_id }) : t('dashboard:orders.counterLabel'));
        const itemsSummary = order.order_items.map((item) => `${item.quantity}x ${item.name}`).join(", ");

        let sourceLabel = t('dashboard:alerts.source.order');
        if (order.source === "QR") {
          sourceLabel = t('dashboard:alerts.source.qr');
        } else if (order.source === "COUNTER") {
          sourceLabel = t('dashboard:alerts.source.counter');
        }

        return {
          id: order.id,
          code: order.code,
          totalLabel: localeFormatCurrency(order.total_cents, dashboardCurrency, { isMinorUnit: true }),
          timeLabel: formatRelative(order.created_at),
          tableLabel,
          status: order.status,
          statusLabel: t(`dashboard:orders.status.${order.status.toLowerCase()}`, { defaultValue: formatStatusLabel(order.status) }),
          source: order.source,
          sourceLabel,
          itemsSummary: itemsSummary || null,
        } as AlertEntry;
      });
  }, [ordersQuery.data, tableLookup, t, formatRelative, localeFormatCurrency, dashboardCurrency]);

  useEffect(() => {
    if (alertEntries.length === 0) {
      setAcknowledgedAlertIds((prev) => (prev.size === 0 ? prev : new Set<string>()));
      return;
    }

    setAcknowledgedAlertIds((prev) => {
      if (prev.size === 0) {
        return prev;
      }

      const activeIds = new Set(alertEntries.map((alert) => alert.id));
      let changed = false;
      const next = new Set<string>();

      prev.forEach((id) => {
        if (activeIds.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });

      if (!changed && next.size === prev.size) {
        return prev;
      }

      return next;
    });
  }, [alertEntries]);

  const visibleAlertEntries = useMemo(() => {
    if (acknowledgedAlertIds.size === 0) {
      return alertEntries;
    }

    return alertEntries.filter((alert) => !acknowledgedAlertIds.has(alert.id));
  }, [alertEntries, acknowledgedAlertIds]);

  const activeAlertOrder = useMemo(() => {
    if (!alertOrderId) {
      return null;
    }
    return ((ordersQuery.data as OrderRecord[]) ?? []).find((order) => order.id === alertOrderId) ?? null;
  }, [alertOrderId, ordersQuery.data]);

  const seenAlertsCount = alertEntries.length - visibleAlertEntries.length;
  const derivedUnseenNotifications = Math.max(0, derivedData.notifications - seenAlertsCount);

  const canAcceptAlertOrder = activeAlertOrder
    ? ACCEPTABLE_ALERT_STATUSES.has(activeAlertOrder.status) && !isAlertActionPending
    : false;
  const alertOrderStatusLabel = activeAlertOrder
    ? t(`dashboard:orders.status.${activeAlertOrder.status.toLowerCase()}`, {
        defaultValue: formatStatusLabel(activeAlertOrder.status),
      })
    : null;
  const alertOrderTableLabel = activeAlertOrder
    ? activeAlertOrder.table_id
      ? tableLookup.get(activeAlertOrder.table_id)?.name ??
        t('dashboard:orders.tablePrefix', {
          defaultValue: 'Table {{number}}',
          number: activeAlertOrder.table_id,
        })
      : t('dashboard:orders.counterLabel')
    : null;
  const alertOrderSourceLabel = activeAlertOrder
    ? activeAlertOrder.source === 'QR'
      ? t('dashboard:alerts.source.qr')
      : activeAlertOrder.source === 'COUNTER'
        ? t('dashboard:alerts.source.counter')
        : t('dashboard:alerts.source.order')
    : null;
  const alertOrderTimeLabel = activeAlertOrder ? formatRelative(activeAlertOrder.created_at) : null;
  const alertOrderTotalLabel = activeAlertOrder
    ? localeFormatCurrency(activeAlertOrder.total_cents, dashboardCurrency, { isMinorUnit: true })
    : null;
  const alertOrderItems = activeAlertOrder?.order_items ?? [];
  const alertOrderItemCount = alertOrderItems.reduce(
    (sum, item) => sum + (typeof item.quantity === 'number' ? item.quantity : 0),
    0,
  );

  const handleAlertPeekOpenChange = useCallback(
    (open: boolean) => {
      setIsAlertPeekOpen(open);
      if (!open) {
        setAlertOrderId(null);
        setIsAlertActionPending(false);
      }
    },
    [],
  );

  const handleAlertOrderOpenFull = useCallback(() => {
    setIsAlertPeekOpen(false);
    setAlertOrderId(null);
    navigate(tenantPath("orders"));
  }, [navigate, tenantPath]);

  const handleAlertAcknowledged = useCallback((orderId: string) => {
    setAcknowledgedAlertIds((prev) => {
      if (prev.has(orderId)) {
        return prev;
      }

      const next = new Set(prev);
      next.add(orderId);
      return next;
    });
  }, []);

  const handleClearAllAlerts = useCallback(() => {
    if (alertEntries.length === 0) {
      return;
    }

    setAcknowledgedAlertIds(() => new Set(alertEntries.map((alert) => alert.id)));
  }, [alertEntries]);

  const notificationsCount = Math.max(derivedUnseenNotifications, visibleAlertEntries.length);

  const lastUpdatedTimestamp = Math.max(
    ordersQuery.dataUpdatedAt ?? 0,
    tablesQuery.dataUpdatedAt ?? 0,
    salesReportQuery.dataUpdatedAt ?? 0,
    itemsReportQuery.dataUpdatedAt ?? 0,
  );
  const lastUpdatedAt = lastUpdatedTimestamp ? new Date(lastUpdatedTimestamp) : null;

  const quickActions = useMemo<QuickAction[]>(() => {
    const canManageStaff = staffInfo?.role === "OWNER" || staffInfo?.role === "MANAGER";

    const navigateTo = (path: string) => navigate(tenantPath(path));

    return [
      {
        id: "counter-order",
        title: t("dashboard:quickActions.actions.counterOrder.title"),
        description: t("dashboard:quickActions.actions.counterOrder.description"),
        icon: <ShoppingBag className="h-4 w-4" />,
        onClick: () => navigateTo("sales/new"),
      },
      {
        id: "kitchen",
        title: t("dashboard:quickActions.actions.kitchen.title"),
        description: t("dashboard:quickActions.actions.kitchen.description"),
        icon: <ChefHat className="h-4 w-4" />,
        onClick: () => navigateTo("kitchen"),
      },
      {
        id: "menu",
        title: t("dashboard:quickActions.actions.menu.title"),
        description: t("dashboard:quickActions.actions.menu.description"),
        icon: <Package className="h-4 w-4" />,
        onClick: () => navigateTo("menu"),
      },
      {
        id: "qr",
        title: t("dashboard:quickActions.actions.qrCodes.title"),
        description: t("dashboard:quickActions.actions.qrCodes.description"),
        icon: <QrCode className="h-4 w-4" />,
        onClick: () => navigateTo("qr-codes"),
      },
      {
        id: "reports",
        title: t("dashboard:quickActions.actions.reports.title"),
        description: t("dashboard:quickActions.actions.reports.description"),
        icon: <FileText className="h-4 w-4" />,
        onClick: () => navigateTo("reports"),
      },
      {
        id: "staff",
        title: t("dashboard:quickActions.actions.staff.title"),
        description: t("dashboard:quickActions.actions.staff.description"),
        icon: <Users className="h-4 w-4" />,
        onClick: () => {
          if (!canManageStaff) {
            toast({
              title: t("dashboard:quickActions.toasts.restrictedTitle"),
              description: t("dashboard:quickActions.toasts.restrictedDescription"),
            });
            return;
          }
          navigateTo("settings/staff");
        },
      },
    ];
  }, [navigate, tenantPath, staffInfo?.role, toast, t]);

  const invalidateOrderQueries = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['orders', 'list'] });
    queryClient.invalidateQueries({ queryKey: ['orders', 'recent'] });
    queryClient.invalidateQueries({ queryKey: ['kds', 'orders'] });
    queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_ROOT });
  }, [queryClient]);

  const cashSettlementMutation = useMutation({
    mutationFn: async ({ orderId, amount }: { orderId: string; amount: number }) =>
      payOrderCash(orderId, { amount }),
    onMutate: ({ orderId }) => {
      setCashSettlementOrderId(orderId);
    },
    onSuccess: async (response, variables) => {
      const payment = response.payment;
      const code = payment.orderCode ?? variables.orderId;
      toast({
        title: t('dashboard:qrManager.paymentActions.cashToastSuccessTitle', { defaultValue: 'Cash collected' }),
        description: t('dashboard:qrManager.paymentActions.cashToastSuccessDescription', {
          defaultValue: 'Order {{order}} is now marked as paid.',
          order: code,
        }),
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['orders', 'list'] }),
        queryClient.invalidateQueries({ queryKey: ['orders', 'recent'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-v3', 'orders'] }),
        queryClient.invalidateQueries({ queryKey: ['payments'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-v3', 'payments'] }),
        queryClient.invalidateQueries({ queryKey: ['tables'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-v3', 'tables'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-v3', 'table-activity'] }),
        queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_ROOT }),
      ]);
    },
    onError: (error: unknown) => {
      toast({
        title: t('dashboard:qrManager.paymentActions.cashToastErrorTitle', { defaultValue: 'Unable to mark paid' }),
        description:
          error instanceof Error
            ? error.message
            : t('dashboard:qrManager.paymentActions.cashToastErrorDescription', {
                defaultValue: 'Try again in a moment.',
              }),
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setCashSettlementOrderId(null);
    },
  });

  const handleDashboardCashPayment = React.useCallback(
    ({ orderId, amountCents }: { tableId: string; orderId: string; amountCents: number }) => {
      const normalizedAmount = Math.max(0, Math.round(amountCents ?? 0));
      if (!normalizedAmount) {
        toast({
          title: t('dashboard:qrManager.paymentActions.cashToastErrorTitle', { defaultValue: 'Unable to mark paid' }),
          description: t('dashboard:qrManager.paymentActions.noAmount', {
            defaultValue: 'No outstanding amount detected for this order.',
          }),
          variant: 'destructive',
        });
        return;
      }
      cashSettlementMutation.mutate({ orderId, amount: normalizedAmount });
    },
    [cashSettlementMutation, t, toast],
  );

  const handleRecentOrderStatusUpdate = React.useCallback(
    async (orderId: string, nextStatus: OrderStatus): Promise<boolean> => {
      try {
        await updateOrderStatus(orderId, { status: nextStatus });
        const statusLabel = t(`dashboard:orders.status.${nextStatus.toLowerCase()}`, {
          defaultValue: formatStatusLabel(nextStatus),
        });
        toast({
          title: t('dashboard:recentOrders.toasts.statusUpdatedTitle', { defaultValue: 'Order updated' }),
          description: t('dashboard:recentOrders.toasts.statusUpdatedDescription', {
            defaultValue: 'Order marked as {{status}}',
            status: statusLabel,
          }),
        });
        invalidateOrderQueries();
        return true;
      } catch (error) {
        console.error('[dashboard] failed to update order status', error);
        toast({
          title: t('dashboard:recentOrders.toasts.statusFailedTitle', { defaultValue: 'Update failed' }),
          description:
            error instanceof Error
              ? error.message
              : t('dashboard:recentOrders.toasts.statusFailedDescription', {
                  defaultValue: 'Could not update the order status. Please try again.',
                }),
          variant: 'destructive',
        });
        return false;
      }
    },
    [invalidateOrderQueries, t, toast],
  );

  const handleAcceptAlertOrder = useCallback(async () => {
    if (!activeAlertOrder || !ACCEPTABLE_ALERT_STATUSES.has(activeAlertOrder.status)) {
      return;
    }
    setIsAlertActionPending(true);
    try {
      const orderId = activeAlertOrder.id;
      const wasSuccessful = await handleRecentOrderStatusUpdate(orderId, 'ACCEPTED');
      if (wasSuccessful) {
        handleAlertAcknowledged(orderId);
        setIsAlertPeekOpen(false);
        setAlertOrderId(null);
      }
    } finally {
      setIsAlertActionPending(false);
    }
  }, [activeAlertOrder, handleRecentOrderStatusUpdate, handleAlertAcknowledged]);

  const handleRecentOrderPrint = React.useCallback(
    async (orderId: string) => {
      const escapeHtml = (value: string | null | undefined) =>
        (value ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');

      try {
        const receiptText = {
          title: t('dashboard:recentOrders.receipt.title', { defaultValue: 'Order Receipt' }),
          orderLabel: t('recentOrders.columns.code'),
          statusLabel: t('recentOrders.columns.status'),
          tableLabel: t('recentOrders.columns.table'),
          customerLabel: t('dashboard:recentOrders.receipt.customer', { defaultValue: 'Customer' }),
          paymentLabel: t('dashboard:recentOrders.receipt.paymentMethod', { defaultValue: 'Payment' }),
          dateLabel: t('dashboard:recentOrders.receipt.date', { defaultValue: 'Date' }),
          timeLabel: t('dashboard:recentOrders.receipt.time', { defaultValue: 'Time' }),
          itemsLabel: t('recentOrders.columns.items'),
          subtotalLabel: t('dashboard:recentOrders.receipt.subtotal', { defaultValue: 'Subtotal' }),
          taxLabel: t('dashboard:recentOrders.receipt.tax', { defaultValue: 'Tax' }),
          serviceLabel: t('dashboard:recentOrders.receipt.service', { defaultValue: 'Service' }),
          tipLabel: t('dashboard:recentOrders.receipt.tip', { defaultValue: 'Tip' }),
          totalLabel: t('recentOrders.columns.total'),
          thankYou: t('dashboard:recentOrders.receipt.thankYou', { defaultValue: 'Thank you for your business!' }),
        };

        const response = await fetchOrders({ id: orderId, limit: 1, pageSize: 1 });
        const order: OrderSummary | undefined = response.orders[0];

        if (!order) {
          throw new Error(t('dashboard:recentOrders.toasts.receiptOrderMissing', { defaultValue: 'Order not found.' }));
        }

        const currency = (order.totals?.currency ?? dashboardCurrency) || 'USD';
        const formatMoney = (cents?: number | null) =>
          localeFormatCurrency(cents ?? 0, currency, { isMinorUnit: true });

        const lineItemsHtml = (order.items ?? [])
          .map((item) => {
            const itemTotal = (item.priceCents ?? 0) * (item.quantity ?? 0);
            return `
              <div class="item-line">
                <span>${escapeHtml(`${item.quantity ?? 0}x ${item.name}`)}</span>
                <span>${formatMoney(itemTotal)}</span>
              </div>
            `;
          })
          .join('') ||
          `<div class="item-line">${escapeHtml(t('dashboard:recentOrders.receipt.noItems', { defaultValue: 'No items' }))}</div>`;
        const now = new Date();

        const totals = order.totals ?? {
          subtotalCents: order.subtotalCents ?? 0,
          taxCents: order.taxCents ?? 0,
          serviceCents: order.serviceCents ?? 0,
          tipCents: order.tipCents ?? 0,
          totalCents: order.totalCents ?? 0,
          currency,
        };

        const receiptHtml = `
          <html>
            <head>
              <title>${escapeHtml(order.code ?? order.id)}</title>
              <style>
                body {
                  font-family: 'Courier New', monospace;
                  width: 320px;
                  margin: 0 auto;
                  padding: 16px;
                  line-height: 1.4;
                  color: #111;
                }
                .header { text-align: center; margin-bottom: 12px; }
                .section { margin-bottom: 12px; }
                .divider { border-top: 1px dashed #999; margin: 12px 0; }
                .item-line { display: flex; justify-content: space-between; margin-bottom: 6px; }
                .totals { margin-top: 12px; }
                .totals div { display: flex; justify-content: space-between; margin-bottom: 4px; }
                .total { font-weight: 700; border-top: 1px solid #000; padding-top: 8px; margin-top: 8px; }
                h2, h3 { margin: 0; font-size: 16px; }
                p { margin: 2px 0; }
                @media print { body { width: auto; margin: 0; padding: 10px; } }
              </style>
            </head>
            <body>
              <div class="header">
                <h2>${escapeHtml(restaurantData?.name ?? t('dashboard:hero.userFallback', { defaultValue: 'Restaurant' }))}</h2>
                <p>${escapeHtml(receiptText.title)}</p>
              </div>
              <div class="section">
                <p><strong>${escapeHtml(receiptText.orderLabel)}:</strong> ${escapeHtml(order.code ?? order.id)}</p>
                <p><strong>${escapeHtml(receiptText.statusLabel)}:</strong> ${escapeHtml(order.status)}</p>
                <p><strong>${escapeHtml(receiptText.tableLabel)}:</strong> ${escapeHtml(order.table ?? order.tableNumber?.toString() ?? t('recentOrders.receipt.tableFallback', { defaultValue: 'Counter order' }))}</p>
                ${order.customerName ? `<p><strong>${escapeHtml(receiptText.customerLabel)}:</strong> ${escapeHtml(order.customerName)}</p>` : ''}
                ${order.paymentMethod ? `<p><strong>${escapeHtml(receiptText.paymentLabel)}:</strong> ${escapeHtml(order.paymentMethod)}</p>` : ''}
                <p><strong>${escapeHtml(receiptText.dateLabel)}:</strong> ${escapeHtml(now.toLocaleDateString())}</p>
                <p><strong>${escapeHtml(receiptText.timeLabel)}:</strong> ${escapeHtml(now.toLocaleTimeString())}</p>
              </div>
              <div class="divider"></div>
              <div class="section">
                <h3>${escapeHtml(receiptText.itemsLabel)}</h3>
                ${lineItemsHtml}
              </div>
              <div class="divider"></div>
              <div class="section totals">
                <div><span>${escapeHtml(receiptText.subtotalLabel)}</span><span>${formatMoney(totals.subtotalCents)}</span></div>
                <div><span>${escapeHtml(receiptText.taxLabel)}</span><span>${formatMoney(totals.taxCents)}</span></div>
                <div><span>${escapeHtml(receiptText.serviceLabel)}</span><span>${formatMoney(totals.serviceCents)}</span></div>
                <div><span>${escapeHtml(receiptText.tipLabel)}</span><span>${formatMoney(totals.tipCents)}</span></div>
                <div class="total"><span>${escapeHtml(receiptText.totalLabel)}</span><span>${formatMoney(totals.totalCents)}</span></div>
              </div>
              <div class="divider"></div>
              <div class="section" style="text-align: center;">
                <p>${escapeHtml(receiptText.thankYou)}</p>
              </div>
            </body>
          </html>
        `;

        const printWindow = window.open('', '_blank', 'width=420,height=680,noopener');
        if (!printWindow) {
          toast({
            title: t('dashboard:recentOrders.toasts.receiptFailedTitle', { defaultValue: 'Print blocked' }),
            description: t('dashboard:recentOrders.toasts.receiptPopupBlocked', {
              defaultValue: 'Please allow pop-ups to print receipts.',
            }),
            variant: 'destructive',
          });
          return;
        }

        printWindow.document.write(receiptHtml);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          try {
            printWindow.print();
          } catch (printError) {
            console.error('[dashboard] print command failed', printError);
          }
        }, 50);

        toast({
          title: t('dashboard:recentOrders.toasts.receiptPrintedTitle', { defaultValue: 'Receipt ready' }),
          description: t('dashboard:recentOrders.toasts.receiptPrintedDescription', {
            defaultValue: 'Receipt window opened for printing.',
          }),
        });
      } catch (error) {
        console.error('[dashboard] failed to print receipt', error);
        toast({
          title: t('dashboard:recentOrders.toasts.receiptFailedTitle', { defaultValue: 'Print failed' }),
          description:
            error instanceof Error
              ? error.message
              : t('dashboard:recentOrders.toasts.receiptFailedDescription', {
                  defaultValue: 'Unable to prepare the receipt. Please try again.',
                }),
          variant: 'destructive',
        });
      }
    },
    [dashboardCurrency, localeFormatCurrency, restaurantData?.name, t, toast],
  );

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      // Clear shown errors when manually refreshing to allow new errors to be shown
      shownErrorsRef.current.clear();
      await queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_ROOT });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleAlertOrderView = (orderId: string) => {
    setAlertsOpen(false);
    const orders = ((ordersQuery.data as OrderRecord[]) ?? []);
    const match = orders.find((order) => order.id === orderId);
    if (match) {
      setAlertOrderId(orderId);
      setIsAlertPeekOpen(true);
      return;
    }
    setAlertOrderId(null);
    navigate(tenantPath("orders"));
  };

  const handleViewAllOrders = () => {
    setAlertsOpen(false);
    navigate(tenantPath("orders"));
  };

  const handleLaunchCounterOrder = useCallback(() => {
    setCounterPeekKey((prev) => prev + 1);
    setIsCounterPeekOpen(true);
  }, [setCounterPeekKey, setIsCounterPeekOpen]);

  useEffect(() => {
    if (!isAlertPeekOpen) {
      return;
    }
    if (alertOrderId && !activeAlertOrder) {
      setIsAlertPeekOpen(false);
      setAlertOrderId(null);
      setIsAlertActionPending(false);
    }
  }, [isAlertPeekOpen, alertOrderId, activeAlertOrder]);

  // Avoid flashing the skeleton during background refetches by requiring missing data.
  const initialAuthPending = authLoading && !user;

  const initialDataPending =
    Boolean(tenantSlug) &&
    (
      (ordersQuery.isLoading && !ordersQuery.data) ||
      (tablesQuery.isLoading && !tablesQuery.data) ||
      (tableActivityQuery.isLoading && !tableActivityQuery.data) ||
      (paymentsQuery.isLoading && !paymentsQuery.data) ||
      (salesReportQuery.isLoading && !salesReportQuery.data) ||
      (itemsReportQuery.isLoading && !itemsReportQuery.data)
    );

  useEffect(() => {
    if (!(import.meta.env.DEV && import.meta.env.VITE_DASHBOARD_DEBUG === '1')) {
      return;
    }
    console.log("[dashboard:bootstrap]", {
      tenantSlug,
      isBootstrapped,
      initialAuthPending,
      initialDataPending,
      authLoading,
      hasUser: Boolean(user),
      roleLoading,
      hasStaffInfo: Boolean(staffInfo),
      tenantLoading,
      hasRestaurantData: Boolean(restaurantData),
      queries: {
        orders: {
          isLoading: ordersQuery.isLoading,
          hasData: Boolean(ordersQuery.data),
          isError: ordersQuery.isError,
          error: ordersQuery.error instanceof Error ? ordersQuery.error.message : ordersQuery.error,
        },
        tables: {
          isLoading: tablesQuery.isLoading,
          hasData: Boolean(tablesQuery.data),
          isError: tablesQuery.isError,
          error: tablesQuery.error instanceof Error ? tablesQuery.error.message : tablesQuery.error,
        },
        tableActivity: {
          isLoading: tableActivityQuery.isLoading,
          hasData: Boolean(tableActivityQuery.data),
          isError: tableActivityQuery.isError,
          error:
            tableActivityQuery.error instanceof Error
              ? tableActivityQuery.error.message
              : tableActivityQuery.error,
        },
        payments: {
          isLoading: paymentsQuery.isLoading,
          hasData: Boolean(paymentsQuery.data),
          isError: paymentsQuery.isError,
          error: paymentsQuery.error instanceof Error ? paymentsQuery.error.message : paymentsQuery.error,
        },
        sales: {
          isLoading: salesReportQuery.isLoading,
          hasData: Boolean(salesReportQuery.data),
          isError: salesReportQuery.isError,
          error: salesReportQuery.error instanceof Error ? salesReportQuery.error.message : salesReportQuery.error,
        },
        items: {
          isLoading: itemsReportQuery.isLoading,
          hasData: Boolean(itemsReportQuery.data),
          isError: itemsReportQuery.isError,
          error: itemsReportQuery.error instanceof Error ? itemsReportQuery.error.message : itemsReportQuery.error,
        },
        staff: {
          isLoading: staffQuery.isLoading,
          hasData: Boolean(staffQuery.data),
          isError: staffQuery.isError,
          error: staffQuery.error instanceof Error ? staffQuery.error.message : staffQuery.error,
        },
        qr: {
          isLoading: qrMetricsQuery.isLoading,
          hasData: Boolean(qrMetricsQuery.data),
          isError: qrMetricsQuery.isError,
          error: qrMetricsQuery.error instanceof Error ? qrMetricsQuery.error.message : qrMetricsQuery.error,
        },
      },
    });
  }, [
    tenantSlug,
    isBootstrapped,
    initialAuthPending,
    initialDataPending,
    authLoading,
    user,
    roleLoading,
    staffInfo,
    tenantLoading,
    restaurantData,
    ordersQuery.isLoading,
    ordersQuery.data,
    ordersQuery.isError,
    ordersQuery.error,
    tablesQuery.isLoading,
    tablesQuery.data,
    tablesQuery.isError,
    tablesQuery.error,
    tableActivityQuery.isLoading,
    tableActivityQuery.data,
    tableActivityQuery.isError,
    tableActivityQuery.error,
    paymentsQuery.isLoading,
    paymentsQuery.data,
    paymentsQuery.isError,
    paymentsQuery.error,
    salesReportQuery.isLoading,
    salesReportQuery.data,
    salesReportQuery.isError,
    salesReportQuery.error,
    itemsReportQuery.isLoading,
    itemsReportQuery.data,
    itemsReportQuery.isError,
    itemsReportQuery.error,
    staffQuery.isLoading,
    staffQuery.data,
    staffQuery.isError,
    staffQuery.error,
    qrMetricsQuery.isLoading,
    qrMetricsQuery.data,
    qrMetricsQuery.isError,
    qrMetricsQuery.error,
  ]);
  // Mark the dashboard ready once we have the minimum data; keep showing the skeleton until then.
  useEffect(() => {
    if (!isBootstrapped && !initialAuthPending && !initialDataPending) {
      setIsBootstrapped(true);
    }
  }, [isBootstrapped, initialAuthPending, initialDataPending]);

  useEffect(() => {
    if (isBootstrapped) {
      return;
    }
    if (initialAuthPending) {
      return;
    }
    const timer = window.setTimeout(() => {
      setIsBootstrapped((current) => current || true);
    }, 3500);
    return () => {
      window.clearTimeout(timer);
    };
  }, [isBootstrapped, initialAuthPending]);

  if (!isBootstrapped) {
    return <DashboardSkeleton />;
  }


  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30">
        <Card className="w-full max-w-md">
          <CardContent className="space-y-4 pt-8 text-center">
            <h2 className="text-xl font-semibold">{t("dashboard:emptyState.authRequiredTitle")}</h2>
            <p className="text-muted-foreground">{t("dashboard:emptyState.authRequiredSubtitle")}</p>
            <Button onClick={() => (window.location.href = "/auth/login")}>{t("common:buttons.signIn")}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!restaurantData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30">
        <Card className="w-full max-w-md">
          <CardContent className="space-y-4 pt-8 text-center">
            <h2 className="text-xl font-semibold">{t("dashboard:emptyState.noRestaurantTitle")}</h2>
            <p className="text-muted-foreground">{t("dashboard:emptyState.noRestaurantSubtitle")}</p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              {t("common:buttons.reload")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const effectiveStaff =
    staffInfo ?? {
      id: "fallback",
      restaurant_id: restaurantData.restaurant_id,
      role: "OWNER" as const,
      name: user.email?.split("@")[0] ?? t('dashboard:hero.userFallback'),
      email: user.email ?? "",
      is_active: true,
    };
  return (
    <div className="space-y-8 p-6 pb-12">
      <section className="relative overflow-hidden rounded-3xl border border-gray-200/70 bg-gradient-to-br from-white via-gray-50/70 to-white p-8 shadow-xl shadow-gray-500/5 dark:border-gray-700/60 dark:from-gray-900 dark:via-gray-800/60 dark:to-gray-900">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(0,0,0,0.04),transparent_55%)] dark:bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.04),transparent_55%)]" />
        <div className="relative flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl space-y-4 lg:flex-1 lg:min-w-[26rem] lg:pr-6">
            <Badge variant="secondary" className="bg-emerald-100/80 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
              {t("dashboard:hero.liveBadge")}
            </Badge>
            <h1 className="text-4xl font-semibold tracking-tight text-gray-900 dark:text-gray-50">
              {restaurantData.name}
            </h1>
            <p className="text-base text-muted-foreground">
              {t("dashboard:hero.welcome", { defaultValue: "Welcome back, {{name}}", name: effectiveStaff.name })}
            </p>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2">
                <Calendar className="h-4 w-4 text-primary" />
                {new Intl.DateTimeFormat(language, { dateStyle: "full" }).format(today)}
              </span>
              <span className="inline-flex items-center gap-2">
                <Zap className="h-4 w-4 text-emerald-500" />
                <RelativeTimeLabel
                  timestamp={lastUpdatedAt}
                  formatter={formatRelative}
                  fallback={t("common:dates.justNow")}
                />
              </span>
            </div>
          </div>
          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap">
            <Button
              variant="outline"
              className="border border-border bg-card transition-colors hover:bg-muted"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {t("dashboard:hero.refresh", { defaultValue: "Refresh" })}
            </Button>
            <Popover open={isQuickActionsOpen} onOpenChange={setIsQuickActionsOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="border border-border bg-card transition-colors hover:bg-muted"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t("dashboard:hero.quickActions", { defaultValue: "Quick Actions" })}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                sideOffset={8}
                className="w-full max-w-sm p-0 shadow-xl sm:max-w-md"
              >
                <DashboardQuickActions
                  actions={quickActions}
                  title={t("dashboard:quickActions.title")}
                  description={t("dashboard:quickActions.description")}
                  emptyMessage={t("dashboard:quickActions.empty")}
                  onAction={() => setIsQuickActionsOpen(false)}
                />
              </PopoverContent>
            </Popover>
            <Popover open={isQrOrderingPopoverOpen} onOpenChange={setIsQrOrderingPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="border border-border bg-card transition-colors hover:bg-muted"
                >
                  <SlidersHorizontal className="mr-2 h-4 w-4" />
                  {t("dashboard:qrOrdering.triggerLabel", { defaultValue: "Manage QR ordering" })}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                sideOffset={8}
                className="w-full max-w-sm p-0 shadow-xl sm:max-w-md"
              >
                <QrOrderingStatusCard className="border-none bg-transparent shadow-none" />
              </PopoverContent>
            </Popover>
            <Button
              onClick={handleLaunchCounterOrder}
              className="h-12 w-full rounded-2xl bg-gradient-to-br from-primary/90 via-primary to-primary/90 text-sm font-semibold text-white shadow-[0_18px_36px_-22px_rgba(59,130,246,0.65)] transition hover:shadow-[0_20px_42px_-18px_rgba(59,130,246,0.75)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 sm:w-auto"
            >
              <Smartphone className="mr-2 h-4 w-4" />
              {t("dashboard:hero.launchCounter")}
            </Button>
            <Popover open={alertsOpen} onOpenChange={setAlertsOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="relative border border-border bg-card transition-colors hover:bg-muted"
                  aria-haspopup="dialog"
                  aria-expanded={alertsOpen}
                >
                  <Bell className="mr-2 h-4 w-4" />
                  {t("dashboard:hero.alerts", { defaultValue: "Alerts" })}
                  {notificationsCount > 0 ? (
                    <span className="absolute -right-1 -top-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-semibold text-destructive-foreground">
                      {notificationsCount > 9 ? "9+" : notificationsCount}
                    </span>
                  ) : null}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                sideOffset={8}
                className="w-full max-w-sm p-0 shadow-xl sm:max-w-md md:max-w-lg"
              >
                <AlertsPopoverContent
                  alerts={visibleAlertEntries}
                  onViewOrder={handleAlertOrderView}
                  onViewAllOrders={handleViewAllOrders}
                  onClearAll={handleClearAllAlerts}
                  hasAnyAlerts={alertEntries.length > 0}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div className="relative mt-8 grid gap-6 md:grid-cols-2">
          <Card className="border border-border bg-card shadow-lg">
            <CardContent className="flex items-center justify-between gap-6 p-6">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t("dashboard:liveStatus.activeOrders")}</p>
                <p className="text-3xl font-semibold text-gray-900 dark:text-gray-100">
                  {derivedData.liveStatus.waitingOrders}
                </p>
                <p className="text-xs text-muted-foreground">{t("dashboard:liveStatus.activeOrdersSubtitle")}</p>
              </div>
              <div className="text-right">
                <p className="text-xs font-medium text-muted-foreground">{t("dashboard:liveStatus.completedToday")}</p>
                <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-300">
                  {derivedData.liveStatus.completedOrders}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-border bg-card shadow-lg">
            <CardContent className="flex items-center justify-between gap-6 p-6">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t("dashboard:liveStatus.occupancy")}</p>
                <p className="text-3xl font-semibold text-gray-900 dark:text-gray-100">
                  {Math.round(derivedData.liveStatus.occupancy.occupiedPercent)}%
                </p>
                <p className="text-xs text-muted-foreground">{t("dashboard:liveStatus.availablePercent", { defaultValue: "{{value}}% available", value: derivedData.liveStatus.occupancy.availablePercent.toFixed(0) })}</p>
              </div>
              <div className="text-right">
                <p className="text-xs font-medium text-muted-foreground">{t("dashboard:liveStatus.staffOnline")}</p>
                <p className="text-lg font-semibold text-primary">
                  {derivedData.liveStatus.staffOnline}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <MetricsCards
        metrics={derivedData.metrics}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        onViewDetails={(metric) =>
          toast({ title: metric.label, description: t("dashboard:quickActions.toasts.viewFallback") })
        }
      />

      <QRRestaurantManager
        tables={derivedData.tables}
        liveStatus={derivedData.liveStatus}
        performance={derivedData.performance}
        onMarkCashPaid={handleDashboardCashPayment}
        cashSettlementOrderId={cashSettlementOrderId}
        isMarkingCashPaid={cashSettlementMutation.isPending}
      />

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <RecentOrders
            orders={derivedData.recentOrders}
            onViewAll={() => navigate(tenantPath("orders"))}
            onViewOrder={(orderId) => navigate(tenantPath(`orders/${orderId}`))}
            onUpdateStatus={handleRecentOrderStatusUpdate}
            onPrintReceipt={handleRecentOrderPrint}
          />
        </div>
        <TopPerformers items={derivedData.topItems} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <SalesChart
          weekly={derivedData.salesSeries}
          hourly={derivedData.hourlySeries}
          currency={dashboardCurrency}
          locale={language}
        />
        <Card className="border border-border bg-card shadow-lg">
          <CardContent className="space-y-4 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t("dashboard:serviceInsights.title")}</h3>
                <p className="text-sm text-muted-foreground">{t("dashboard:serviceInsights.subtitle")}</p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl border border-border bg-muted p-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{t("dashboard:serviceInsights.successRate.title")}</span>
                  <TrendingUp className="h-4 w-4 text-emerald-500" />
                </div>
                <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {derivedData.performance.successRateLabel}
                </p>
                <p className="text-xs text-muted-foreground">{derivedData.performance.successDeltaLabel}</p>
              </div>
              <div className="rounded-xl border border-border bg-muted p-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{t("dashboard:serviceInsights.avgThroughput.title")}</span>
                  <ClockIcon className="h-4 w-4 text-primary" />
                </div>
                <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {derivedData.performance.avgScanTimeLabel}
                </p>
                <p className="text-xs text-muted-foreground">{derivedData.performance.scanDeltaLabel}</p>
              </div>
              <div className="rounded-xl border border-border bg-muted p-4 text-sm sm:col-span-2">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{t("dashboard:serviceInsights.revenueFocus.title")}</span>
                  <DollarSign className="h-4 w-4 text-primary" />
                </div>
                <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {derivedData.performance.revenueLabel}
                </p>
                <p className="text-xs text-muted-foreground">{derivedData.performance.revenueDeltaLabel}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

     <Dialog open={isCounterPeekOpen} onOpenChange={setIsCounterPeekOpen}>
        <DialogContent className="w-full max-w-5xl overflow-hidden border-0 bg-transparent p-0 shadow-none sm:rounded-3xl">
          <div className="flex h-[85vh] w-full flex-col overflow-hidden rounded-3xl bg-background shadow-2xl">
            <div className="flex items-center justify-between border-b border-border/60 px-6 py-4">
              <div>
                <DialogTitle className="text-lg font-semibold text-foreground">
                  {t("dashboard:hero.launchCounter")}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  {t("dashboard:hero.launchCounterDescription", {
                    defaultValue: "Open the counter order console without leaving the dashboard.",
                  })}
                </DialogDescription>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsCounterPeekOpen(false)}
                aria-label={t("orders:actions.close", { defaultValue: "Close" })}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex-1 bg-muted/20">
              <iframe
                key={counterPeekKey}
                src={counterPeekUrl}
                title={t("dashboard:hero.launchCounter")}
                className="h-full w-full border-0"
                loading="lazy"
                allow="payment"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isAlertPeekOpen} onOpenChange={handleAlertPeekOpenChange}>
        <DialogContent className="max-w-3xl space-y-6">
          {activeAlertOrder ? (
            <>
              <DialogHeader className="space-y-2">
                <DialogTitle className="flex items-center justify-between gap-3">
                  <span>
                    {t('dashboard:alerts.peek.title', {
                      defaultValue: 'Order {{code}}',
                      code: activeAlertOrder.code,
                    })}
                  </span>
                  {alertOrderStatusLabel ? (
                    <Badge variant="outline" className="uppercase tracking-wide text-xs">
                      {alertOrderStatusLabel}
                    </Badge>
                  ) : null}
                </DialogTitle>
                <DialogDescription>
                  {t('dashboard:alerts.peek.description', {
                    defaultValue: 'Review the incoming order and accept it without leaving the dashboard.',
                  })}
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 rounded-lg border border-border bg-muted/10 p-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">
                    {t('dashboard:alerts.peek.table', { defaultValue: 'Table' })}
                  </p>
                  <p className="text-sm font-medium text-foreground">{alertOrderTableLabel}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">
                    {t('dashboard:alerts.peek.source', { defaultValue: 'Source' })}
                  </p>
                  <p className="text-sm font-medium text-foreground">{alertOrderSourceLabel}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">
                    {t('dashboard:alerts.peek.placed', { defaultValue: 'Placed' })}
                  </p>
                  <p className="text-sm font-medium text-foreground">{alertOrderTimeLabel}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">
                    {t('dashboard:alerts.peek.total', { defaultValue: 'Total' })}
                  </p>
                  <p className="text-lg font-semibold text-foreground">{alertOrderTotalLabel}</p>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">
                  {t('dashboard:alerts.peek.items', { defaultValue: 'Items' })}
                  <span className="ml-2 font-semibold normal-case text-foreground">({alertOrderItemCount})</span>
                </p>
                <div className="mt-3 space-y-2">
                  {alertOrderItems.length > 0 ? (
                    alertOrderItems.map((item, index) => (
                      <div
                        key={item.id ?? `${activeAlertOrder.id}-item-${index}`}
                        className="flex items-center justify-between rounded-md border border-border/60 bg-background/80 px-3 py-2 text-sm"
                      >
                        <span className="font-medium text-foreground">
                          {`${typeof item.quantity === 'number' ? item.quantity : 1} x `}
                          {item.name ?? t('dashboard:alerts.peek.unnamedItem', { defaultValue: 'Item' })}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-md border border-dashed border-border/70 px-3 py-4 text-center text-sm text-muted-foreground">
                      {t('dashboard:alerts.peek.itemsEmpty', {
                        defaultValue: 'No line items found for this order.',
                      })}
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-muted-foreground">
                  {t('dashboard:alerts.peek.hint', {
                    defaultValue: 'Accepting moves the order into your active queue.',
                  })}
                </p>
                <div className="flex w-full justify-end gap-2 sm:w-auto">
                  <Button variant="outline" onClick={handleAlertOrderOpenFull} disabled={isAlertActionPending}>
                    {t('dashboard:alerts.peek.viewFull', { defaultValue: 'View in Orders' })}
                  </Button>
                  {canAcceptAlertOrder ? (
                    <Button onClick={handleAcceptAlertOrder} disabled={isAlertActionPending}>
                      {isAlertActionPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('dashboard:alerts.peek.accepting', { defaultValue: 'Accepting...' })}
                        </>
                      ) : (
                        t('dashboard:alerts.peek.accept', { defaultValue: 'Accept order' })
                      )}
                    </Button>
                  ) : (
                    <Button variant="secondary" disabled>
                      {alertOrderStatusLabel ?? t('dashboard:orders.status.accepted', { defaultValue: 'Accepted' })}
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>
                  {t('dashboard:alerts.peek.missingTitle', { defaultValue: 'Order not available' })}
                </DialogTitle>
                <DialogDescription>
                  {t('dashboard:alerts.peek.missingDescription', {
                    defaultValue: 'This order may have been completed or is no longer awaiting action.',
                  })}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="justify-end gap-2">
                <Button variant="outline" onClick={() => handleAlertPeekOpenChange(false)}>
                  {t('common:buttons.close', { defaultValue: 'Close' })}
                </Button>
                <Button onClick={handleAlertOrderOpenFull}>
                  {t('dashboard:alerts.peek.viewFull', { defaultValue: 'View in Orders' })}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RealDashboardPageV3;



